import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ReviewsSection() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const reviews = [
    {
      text: t('reviews.reviews.1.text'),
      author: t('reviews.reviews.1.author'),
      verified: t('reviews.reviews.1.verified'),
      initials: 'JD',
    },
    {
      text: t('reviews.reviews.2.text'),
      author: t('reviews.reviews.2.author'),
      verified: t('reviews.reviews.2.verified'),
      initials: 'SM',
    },
    {
      text: t('reviews.reviews.3.text'),
      author: t('reviews.reviews.3.author'),
      verified: t('reviews.reviews.3.verified'),
      initials: 'RK',
    },
  ];

  return (
    <section className="relative w-full overflow-hidden py-10 bg-background-50">
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white">
              <span className="text-[13px] font-bold text-background-50">G</span>
            </div>
            <div className="flex flex-col items-start">
              <span className="text-[13px] font-semibold text-white tracking-tight leading-none">{t('reviews.label')}</span>
              <span className="text-[11px] text-white/40 leading-none">{t('reviews.labelSub')}</span>
            </div>
          </div>
        </div>

        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-3">
          {t('reviews.title')}
        </h2>
        <p className="text-white/50 text-sm text-center mb-10 max-w-2xl mx-auto leading-relaxed">
          {t('reviews.description')}
        </p>

        <div className="flex items-center justify-center gap-6 mb-12">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <i key={s} className="ri-star-fill text-yellow-400 text-sm w-4 h-4 flex items-center justify-center" />
              ))}
            </div>
            <div className="flex flex-col items-start">
              <span className="text-3xl font-bold text-white leading-none">{t('reviews.rating')}</span>
              <span className="text-[11px] text-white/40">{t('reviews.ratingLabel')}</span>
            </div>
          </div>
          <div className="w-px h-10 bg-white/10" />
          <div className="flex flex-col items-start">
            <span className="text-lg font-bold text-white leading-none">{t('reviews.count')}</span>
            <span className="text-[11px] text-white/40">{t('reviews.countLabel')}</span>
          </div>
          <div className="w-px h-10 bg-white/10" />
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5">
            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-white shrink-0">
              <span className="text-[11px] font-bold text-background-50">G</span>
            </div>
            <span className="text-[11px] font-semibold text-white/70 tracking-wide">{t('reviews.google')}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {reviews.map((review, i) => (
            <div key={i} className="bg-background-50/90 border border-white/10 rounded-2xl p-5 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-yellow-400 text-sm w-4 h-4 flex items-center justify-center" />
                  ))}
                </div>
                <div className="inline-flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center rounded-full bg-white/10 shrink-0">
                    <span className="text-[9px] font-bold text-white/60">G</span>
                  </div>
                  <span className="text-[10px] font-semibold tracking-wide text-white/40">GOOGLE</span>
                </div>
              </div>
              <p className="text-white/70 text-[13px] leading-relaxed">{review.text}</p>
              <div className="flex items-center gap-3 pt-3 border-t border-white/10">
                <div className="w-9 h-9 flex items-center justify-center bg-accent-500/20 rounded-full shrink-0">
                  <span className="text-accent-500 text-xs font-bold">{review.initials}</span>
                </div>
                <div>
                  <p className="text-white text-[13px] font-semibold">{review.author}</p>
                  <p className="text-white/40 text-[11px]">{review.verified}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center gap-2 mb-10">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 border border-white/10 hover:border-white/20 disabled:opacity-40 disabled:cursor-not-allowed text-white/60 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line w-5 h-5 flex items-center justify-center" />
          </button>
          {[1, 2, 3].map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-semibold transition-colors cursor-pointer ${
                page === p ? 'bg-accent-500 text-black' : 'bg-white/5 border border-white/10 text-white/60 hover:border-white/20'
              }`}
            >
              {p}
            </button>
          ))}
          <button
            disabled={page === 3}
            onClick={() => setPage(page + 1)}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 border border-white/10 hover:border-white/20 disabled:opacity-40 disabled:cursor-not-allowed text-white/60 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line w-5 h-5 flex items-center justify-center" />
          </button>
        </div>

        <div className="text-center">
          <a
            href="https://search.google.com/local/writereview?placeid=ChIJN1t_tDeuEmsRUsoyG83frY4"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-3 rounded-full text-sm transition-all duration-200 cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center rounded-full bg-white">
              <span className="text-[10px] font-bold text-background-50">G</span>
            </div>
            {t('reviews.leaveReview')}
          </a>
        </div>
      </div>
    </section>
  );
}